<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsappCatalogAPI from 'dashboard/api/whatsappCatalog';
import ButtonV4 from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

// The catalog is cached on the channel and ships with the inbox payload, so this
// tab renders without a Graph API call. Sync pulls a fresh copy from Meta.
const catalog = ref({ ...(props.inbox.catalog || {}) });
const businessId = ref('');
const newCatalogName = ref('');
const existingCatalogId = ref('');
const isSyncing = ref(false);
const isSaving = ref(false);
const showProductForm = ref(false);

const product = reactive({
  retailer_id: '',
  name: '',
  price: '',
  currency: 'USD',
  image_url: '',
  url: '',
  description: '',
});

const connectedCatalog = computed(() =>
  catalog.value.id ? catalog.value : null
);
const products = computed(() => catalog.value.products || []);
const missingScope = computed(() => catalog.value.missing_scope || '');
const businessIdRequired = computed(() => catalog.value.business_id_required);

const isProductValid = computed(
  () =>
    product.retailer_id &&
    product.name &&
    product.price &&
    product.currency &&
    product.image_url
);

const alertError = (error, fallbackKey) =>
  useAlert(error.response?.data?.error || t(fallbackKey));

// Keep the store copy in step so leaving and reopening the tab shows the synced catalog.
const applyCatalog = data => {
  catalog.value = data || {};
  store.commit('inboxes/SET_INBOXES_ITEM', {
    ...props.inbox,
    catalog: catalog.value,
  });
};

const syncCatalog = async () => {
  isSyncing.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.syncCatalog(props.inbox.id);
    applyCatalog(data.catalog);
    useAlert(t('INBOX_MGMT.WHATSAPP_CATALOG.SYNC_SUCCESS'));
  } catch (error) {
    alertError(error, 'INBOX_MGMT.WHATSAPP_CATALOG.SYNC_ERROR');
  } finally {
    isSyncing.value = false;
  }
};

const createCatalog = async () => {
  if (!newCatalogName.value) return;

  isSaving.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.createCatalog(
      props.inbox.id,
      newCatalogName.value,
      businessId.value || undefined
    );
    newCatalogName.value = '';
    businessId.value = '';
    applyCatalog(data.catalog);
    useAlert(t('INBOX_MGMT.WHATSAPP_CATALOG.CREATE_SUCCESS'));
  } catch (error) {
    alertError(error, 'INBOX_MGMT.WHATSAPP_CATALOG.CREATE_ERROR');
  } finally {
    isSaving.value = false;
  }
};

const connectCatalog = async () => {
  if (!existingCatalogId.value) return;

  isSaving.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.connectCatalog(
      props.inbox.id,
      existingCatalogId.value
    );
    existingCatalogId.value = '';
    applyCatalog(data.catalog);
    useAlert(t('INBOX_MGMT.WHATSAPP_CATALOG.CONNECT_SUCCESS'));
  } catch (error) {
    alertError(error, 'INBOX_MGMT.WHATSAPP_CATALOG.CONNECT_ERROR');
  } finally {
    isSaving.value = false;
  }
};

const createProduct = async () => {
  if (!isProductValid.value) return;

  isSaving.value = true;
  try {
    const { data } = await WhatsappCatalogAPI.createProduct(
      props.inbox.id,
      connectedCatalog.value.id,
      { ...product }
    );
    Object.assign(product, {
      retailer_id: '',
      name: '',
      price: '',
      image_url: '',
      url: '',
      description: '',
    });
    showProductForm.value = false;
    applyCatalog(data.catalog);
    useAlert(t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT_SUCCESS'));
  } catch (error) {
    alertError(error, 'INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT_ERROR');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col max-w-3xl gap-6">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_CATALOG.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_CATALOG.DESCRIPTION') }}
      </p>
    </div>

    <div
      v-if="missingScope"
      class="p-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
    >
      {{
        $t('INBOX_MGMT.WHATSAPP_CATALOG.MISSING_SCOPE', {
          scopes: missingScope,
        })
      }}
    </div>

    <template v-if="connectedCatalog">
      <div
        class="flex items-center justify-between p-4 rounded-lg bg-n-alpha-1"
      >
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ connectedCatalog.name }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              $t('INBOX_MGMT.WHATSAPP_CATALOG.CATALOG_ID', {
                id: connectedCatalog.id,
              })
            }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <ButtonV4
            sm
            faded
            slate
            icon="i-lucide-refresh-cw"
            class="shrink-0"
            :is-loading="isSyncing"
            :disabled="isSyncing"
            :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.SYNC')"
            @click="syncCatalog"
          />
          <ButtonV4
            sm
            faded
            slate
            :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.ADD_PRODUCT')"
            @click="showProductForm = !showProductForm"
          />
        </div>
      </div>

      <div v-if="showProductForm" class="grid grid-cols-2 gap-4">
        <Input
          v-model="product.name"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.NAME')"
        />
        <Input
          v-model="product.retailer_id"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.RETAILER_ID')"
          :message="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.RETAILER_ID_HELP')"
        />
        <Input
          v-model="product.price"
          type="number"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.PRICE')"
        />
        <Input
          v-model="product.currency"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.CURRENCY')"
          placeholder="USD"
        />
        <Input
          v-model="product.image_url"
          class="col-span-2"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.IMAGE_URL')"
          placeholder="https://example.com/product.jpg"
        />
        <Input
          v-model="product.url"
          class="col-span-2"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.URL')"
        />
        <Input
          v-model="product.description"
          class="col-span-2"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.DESCRIPTION')"
        />
        <div class="col-span-2">
          <ButtonV4
            :is-loading="isSaving"
            :disabled="!isProductValid || isSaving"
            :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCT.SAVE')"
            @click="createProduct"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_CATALOG.PRODUCTS') }}
        </span>
        <p v-if="!products.length" class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_CATALOG.NO_PRODUCTS') }}
        </p>
        <div v-else class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div
            v-for="item in products"
            :key="item.id"
            class="flex flex-col gap-1 p-2 border rounded-lg border-n-weak"
          >
            <img
              v-if="item.image_url"
              :src="item.image_url"
              class="object-cover w-full rounded h-24"
              alt=""
            />
            <span class="text-sm truncate text-n-slate-12">{{
              item.name
            }}</span>
            <span class="text-xs text-n-slate-11">{{ item.price }}</span>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex items-start justify-between gap-4">
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_CATALOG.NO_CATALOG') }}
        </p>
        <ButtonV4
          sm
          faded
          slate
          icon="i-lucide-refresh-cw"
          class="shrink-0"
          :is-loading="isSyncing"
          :disabled="isSyncing"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.SYNC')"
          @click="syncCatalog"
        />
      </div>

      <Input
        v-if="businessIdRequired"
        v-model="businessId"
        :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.BUSINESS_ID')"
        :message="$t('INBOX_MGMT.WHATSAPP_CATALOG.BUSINESS_ID_HELP')"
      />

      <div class="flex items-end gap-2">
        <Input
          v-model="newCatalogName"
          class="flex-1"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.CREATE_LABEL')"
          :placeholder="$t('INBOX_MGMT.WHATSAPP_CATALOG.CREATE_PLACEHOLDER')"
        />
        <ButtonV4
          :is-loading="isSaving"
          :disabled="
            !newCatalogName || (businessIdRequired && !businessId) || isSaving
          "
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.CREATE')"
          @click="createCatalog"
        />
      </div>

      <div class="flex items-end gap-2">
        <Input
          v-model="existingCatalogId"
          class="flex-1"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.CONNECT_LABEL')"
          :message="$t('INBOX_MGMT.WHATSAPP_CATALOG.CONNECT_HELP')"
        />
        <ButtonV4
          faded
          slate
          :is-loading="isSaving"
          :disabled="!existingCatalogId || isSaving"
          :label="$t('INBOX_MGMT.WHATSAPP_CATALOG.CONNECT')"
          @click="connectCatalog"
        />
      </div>
    </template>
  </div>
</template>
